// Regras puras do fluxo Normal (Diretoria/Rede) — ports 1:1 do monólito
// src/pages/MinhaEquipe/ApreciarRealizacao/index.tsx. PARIDADE ESTRITA: cada ramo
// (inclusive código morto documentado no catálogo) é preservado como está, com o
// ID da regra do catálogo (.specs/features/fluxo-apreciacao-mad-normal/regras-catalogo.md)
// anotado no ponto correspondente. Nenhum import de React aqui (AD-002).

import { NormalRuleContext, userCodEtapa } from './context';

// Pontuação por multiplicador×status×etapa — port de index.tsx:154-181.
// Tabela: 300 (mult1) / 600 (dobrado N1) / 700 (promovido-concluido N2) /
// 1200 (dobrado N2) / 1300 (promovido-concluido N3) / 1500 (valeu, mult5) /
// 2500 (mult8 N3, +100) / 5000 (mult16 N3, +200).
export function calcPoints(multiplicador: number, status: string, stageNumber: number): number {
  if (
    typeof multiplicador !== 'number' ||
    multiplicador <= 0 ||
    typeof status !== 'string' ||
    typeof stageNumber !== 'number' ||
    stageNumber < 1 ||
    stageNumber > 3
  ) {
    return 0;
  }

  let adicional = 0;

  if (
    ((status === 'promovido' || status === 'concluido') &&
      ((stageNumber === 2 && multiplicador === 2) || (stageNumber === 3 && multiplicador === 4))) ||
    (stageNumber === 3 && multiplicador === 8)
  ) {
    adicional = 100;
  }

  if (stageNumber === 3 && multiplicador === 16) {
    adicional = 200;
  }

  return multiplicador * 300 + adicional;
}

// Etapa da REALIZAÇÃO (STEP-01..03) — cópia LOCAL da régua de src/utils/helpers.tsx
// (getTaskStep). STEP-04: front e backend implementam a mesma régua; esta cópia
// congela a do fluxo Normal para que mudanças no helper global não vazem para cá
// sem decisão explícita.
export function getTaskStepLocal(task: any): number | undefined {
  if (!task || typeof task.status !== 'string') return undefined;

  // STEP-01 — Etapa 2
  if (
    (task.status === 'rejeitado' || task.status === 'aguardando' || task.status === 'ciente') &&
    (typeof task.stageNumber === 'undefined' || task.stageNumber === 0)
  ) {
    return 2;
  }

  // STEP-02 — Se tiver Pit Stop
  if (typeof task.prosseguir_gst === 'string') {
    if (task.prosseguir_gst === 'next' || task.prosseguir_gst === 'stopped') {
      return 4.1;
    } else {
      return 5;
    }
  }

  // STEP-03 — Etapa 3, 4 e 5
  if (typeof task.multiplicador === 'number') {
    if (typeof task.stageNumber === 'number') {
      if (task.stageNumber === 1) {
        return 3;
      } else if (task.stageNumber === 2) {
        return 4;
      } else if (task.stageNumber === 3) {
        return 5;
      }
    } else {
      // Etapa 3
      if (task.multiplicador === 1) {
        return 3;
      }

      // Etapa 3
      if (task.status === 'dobrado' && task.multiplicador === 2) {
        return 3;
      }

      // Etapa 4
      if (task.status === 'promovido' && task.multiplicador === 2) {
        return 4;
      }

      // Etapa 4
      if (task.status === 'dobrado' && task.multiplicador === 4) {
        return 4;
      }

      // Etapa 5
      if (task.status === 'promovido' && task.multiplicador === 4) {
        return 5;
      }

      // Etapa 5
      if (task.status === 'dobrado' && task.multiplicador === 8) {
        return 5;
      }

      // Etapa 5
      if (task.status === 'selo') {
        return 5;
      }
    }
  }

  return undefined;
}

// Posicionamento do GESTOR frente à task (POS-01/03..07) — port de index.tsx:385-459.
// Os ramos om-dupla (original_manager é o próprio gestor OU membro da sua mad_dupla)
// valem também no fluxo Normal: comportamento atual preservado.
export function getPlayerStep(ctx: NormalRuleContext, task: any): number {
  const playerInfo: any = ctx.playerInfo;

  // POS-01 — assessor do pit stop tem prioridade máxima, antes de qualquer cod_etapa
  if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) {
    return 4.1;
  }

  if (typeof userCodEtapa(ctx, null, !!task.mad) === 'number') {
    // POS-03
    if (userCodEtapa(ctx, null, !!task.mad) === 2) {
      return 2;
    }

    // POS-04
    if (userCodEtapa(ctx, null, !!task.mad) === 4) {
      if (
        String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))
      ) {
        if (task.status === 'rejeitado' || task.status === 'aguardando') {
          return 2;
        } else if (
          typeof task.multiplicador === 'number' &&
          (task.multiplicador === 1 || (typeof task.stageNumber === 'number' && task.stageNumber === 1))
        ) {
          return 3;
        } else {
          return 4;
        }
      } else {
        return 4;
      }
    }

    // POS-05
    if (userCodEtapa(ctx, null, !!task.mad) === 5) {
      if (
        String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))
      ) {
        if (task.status === 'rejeitado' || task.status === 'aguardando') {
          return 2;
        } else if (typeof task.stageNumber === 'number' && task.stageNumber === 1) {
          return 3;
        } else if (typeof task.stageNumber === 'number' && task.stageNumber === 2) {
          return 4;
        } else {
          return 5;
        }
      } else {
        return 5;
      }
    }

    // POS-06 — NIV-05: retornos crus do userCodEtapa exigem testar 1 OU 3
    if (userCodEtapa(ctx, null, !!task.mad) === 1 || userCodEtapa(ctx, null, !!task.mad) === 3) {
      if (
        String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))
      ) {
        if (task.status === 'rejeitado' || task.status === 'aguardando') {
          return 2;
        } else {
          return 3;
        }
      } else {
        return 3;
      }
    }

    // POS-07
    return 0;
  }

  return 0;
}

// Prazo de apreciação expirado (EXP-01..07) — port de index.tsx:962-1128.
export function taskAppreciationExpired(ctx: NormalRuleContext, task: any): boolean {
  const { realDatetime } = ctx;
  // `any` local: no monólito o Step é tipado sem opcionais; comparações com data
  // ausente resultam false em runtime — semântica preservada aqui.
  const activeStageObj: any = ctx.activeStageObj;
  const playerInfo: any = ctx.playerInfo;

  const _taskStep = getTaskStepLocal(task);
  const _playerStep = getPlayerStep(ctx, task);

  if (activeStageObj && _taskStep && _playerStep && userCodEtapa(ctx, null, !!task.mad) && playerInfo) {
    // EXP-01 — om-dupla cod4 após step4_start: deveria ter agido antecipadamente
    if (
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      userCodEtapa(ctx, null, !!task.mad) === 4 &&
      realDatetime > activeStageObj.date_evaluation_step4_start
    ) {
      if (task.status === 'aguardando') {
        return true;
      }

      if (task.status === 'aprovado' && task.multiplicador && task.multiplicador === 1) {
        return true;
      }

      if (task.status === 'dobrado' && task.multiplicador && task.multiplicador === 2) {
        return true;
      }
    }

    // EXP-02 — Gerente de equipe
    if (
      task.status === 'aguardando' &&
      realDatetime > activeStageObj.date_evaluation_step2_finish &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        (userCodEtapa(ctx, null, !!task.mad) === 4 || userCodEtapa(ctx, null, !!task.mad) === 5)
      )
    ) {
      return true;
    }

    // EXP-03 — Nível 1
    if (
      task.status === 'aprovado' &&
      task.multiplicador &&
      task.multiplicador === 1 &&
      realDatetime > activeStageObj.date_evaluation_step3_finish &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        (userCodEtapa(ctx, null, !!task.mad) === 4 || userCodEtapa(ctx, null, !!task.mad) === 5)
      )
    ) {
      return true;
    }

    // EXP-04 — Nível 1
    if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      realDatetime > activeStageObj.date_evaluation_step3_finish &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        (userCodEtapa(ctx, null, !!task.mad) === 4 || userCodEtapa(ctx, null, !!task.mad) === 5)
      )
    ) {
      return true;
    }

    // EXP-05 — Nível 2
    if (
      activeStageObj.date_evaluation_step4_finish &&
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      realDatetime > activeStageObj.date_evaluation_step4_finish &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        userCodEtapa(ctx, null, !!task.mad) === 5
      )
    ) {
      return true;
    }

    // EXP-06 — Nível 2
    if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      realDatetime > activeStageObj.date_evaluation_step4_finish &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        userCodEtapa(ctx, null, !!task.mad) === 5
      )
    ) {
      return true;
    }

    // EXP-07 — Nível 3 (sem exceção om)
    if (
      activeStageObj.date_evaluation_step5_finish &&
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      realDatetime > activeStageObj.date_evaluation_step5_finish
    ) {
      return true;
    }

    // EXP-07 — Nível 3 (sem exceção om)
    if (
      activeStageObj.date_evaluation_step5_finish &&
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 8 &&
      realDatetime > activeStageObj.date_evaluation_step5_finish
    ) {
      return true;
    }
  }

  return false;
}

// Gate geral de interação (R-01..R-20) — port de index.tsx:1149-1429. Apesar do nome
// histórico, não vale só para reset: é o portão de TODA ação sobre a task.
export function canReset(ctx: NormalRuleContext, task: any): boolean {
  const { cyclesExtended, realDatetime, isVacationManager, getActiveEvaluationStep } = ctx;
  // `any` locais: no monólito playerInfo/activeStageObj são any/Step sem opcionais;
  // comparações com campos ausentes devem manter a semântica JS original.
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  if (!cyclesExtended) return false;

  // OM-03 — Gestor original direto de Nível 2/3 (cod_etapa/cod_etapa_sub 4 ou 5) aprecia a
  // realização de subordinado direto desde o primeiro dia de apreciação (step2_start)
  // do MÊS em que ela foi cadastrada — e não a partir do step2_start da fase trimestral/
  // semestral, que nos "buracos" entre janelas aponta para um mês futuro e barraria o
  // gestor indevidamente. O teto continua sendo step4_finish (cod4) / step5_finish (cod5),
  // garantido pelos ramos específicos de original_manager mais abaixo.
  const isDirectOriginalHighLevelManager =
    (String(task.original_manager) === String(playerInfo?._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo?.mad_dupla?.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    (userCodEtapa(ctx, null, !!task.mad) === 4 || userCodEtapa(ctx, null, !!task.mad) === 5);

  // Piso = step2_start do mês da realização (ver getTaskMonthStep2Start).
  const taskMonthStep2Start = isDirectOriginalHighLevelManager ? getTaskMonthStep2Start(ctx, task) : undefined;

  const evaluationFloor =
    isDirectOriginalHighLevelManager && typeof taskMonthStep2Start === 'number'
      ? taskMonthStep2Start
      : activeStageObj?.date_evaluation_step2_start;

  // R-01/R-02/R-03 — bloqueio imediato, piso temporal (piso undefined NÃO bloqueia) e teto
  if (
    !activeStageObj ||
    !getTaskStepLocal(task) ||
    !playerInfo ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    !task.original_manager ||
    (typeof evaluationFloor === 'number' && realDatetime < evaluationFloor) ||
    realDatetime > activeStageObj.date_evaluation_step5_finish
  ) {
    return false;
  }

  // R-04/LAT-05 — Gestor imediato apreciando realização expirada (decide ANTES dos
  // ramos normais; se nenhum sub-ramo casar, bloqueia o substituto por completo)
  if (
    taskAppreciationExpired(ctx, task) &&
    isVacationManager &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) && userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor)))
  ) {
    if (
      !getActiveEvaluationStep &&
      ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && realDatetime < activeStageObj.date_evaluation_step4_start) ||
        (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && realDatetime < activeStageObj.date_evaluation_step5_start))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 3 &&
        (userCodEtapa(ctx, userInfo, !!task.mad) === 1 || userCodEtapa(ctx, userInfo, !!task.mad) === 3)) ||
        (getActiveEvaluationStep === 3 &&
          userCodEtapa(ctx, userInfo, !!task.mad) === 4 &&
          (userCodEtapa(ctx, playerInfo, !!task.mad) === 1 ||
            // Gerente de equipe substituído (cod_etapa 2): valida realização expirada na step3
            userCodEtapa(ctx, playerInfo, !!task.mad) === 2 ||
            userCodEtapa(ctx, playerInfo, !!task.mad) === 3)) ||
        (getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
        (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      userCodEtapa(ctx, null, !!task.mad) === 4 &&
      realDatetime > activeStageObj.date_evaluation_step4_start &&
      (getTaskStepLocal(task) as number) < 4
    ) {
      return true;
    }

    return false;
  }

  // R-05 — om-dupla cod5: age até step5_finish (independe de janela; vale em buraco)
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo?.mad_dupla?.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 5 &&
    realDatetime <= activeStageObj.date_evaluation_step5_finish
  ) {
    return true;
  }

  // R-06 — om-dupla cod4: age até step4_finish (independe de janela; vale em buraco)
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo?.mad_dupla?.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 4 &&
    realDatetime <= activeStageObj.date_evaluation_step4_finish
  ) {
    return true;
  }

  // R-07 — sem janela ativa ou fase sem type
  if (!getActiveEvaluationStep || !activeStageObj.type) {
    return false;
  }

  // R-08 — coerência janela×tipo de fase
  if (
    (getActiveEvaluationStep < 4 && activeStageObj.type !== 'month') ||
    (getActiveEvaluationStep >= 4 && getActiveEvaluationStep < 5 && activeStageObj.type !== 'trimester') ||
    (getActiveEvaluationStep >= 5 && activeStageObj.type !== 'semester')
  ) {
    return false;
  }

  // R-21 — TRAVA MAD REMOVIDA (2026-05-28): o parceiro em non_evaluator segue a mesma
  // lógica do original_manager; a concorrência é 100% dos guards (CON-*).

  // R-09 — janela 2 em fase semester/trimester, exceto om-dupla cod5 (na prática o om
  // cod5 já saiu em R-05 e o R-08 intercepta os demais; ramo preservado por paridade)
  if (
    getActiveEvaluationStep === 2 &&
    activeStageObj.type &&
    (activeStageObj.type === 'semester' || activeStageObj.type === 'trimester') &&
    !(
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      userCodEtapa(ctx, null, !!task.mad) === 5
    )
  ) {
    return false;
  }

  // R-10 — janela 4 com taskStep<4 (ou fase não-trimester), exceto om-dupla cod5
  if (
    getActiveEvaluationStep === 4 &&
    (((getTaskStepLocal(task) as number) < 4 &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        userCodEtapa(ctx, null, !!task.mad) === 5
      )) ||
      (activeStageObj.type &&
        activeStageObj.type !== 'trimester' &&
        !(
          (String(task.original_manager) === String(playerInfo._id) ||
            (Array.isArray(playerInfo?.mad_dupla) &&
              playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
          userCodEtapa(ctx, null, !!task.mad) === 5
        )))
  ) {
    return false;
  }

  // Janela 5 fora de fase semestral (redundante com R-08; preservado por paridade)
  if (getActiveEvaluationStep === 5 && activeStageObj.type && activeStageObj.type !== 'semester') {
    return false;
  }

  // R-11 — om-dupla cod4 nas janelas 2/3/4 (ramo VIVO; torna o ramo espelhado mais
  // abaixo — om cod4 janelas 2/3 por taskStep — inalcançável)
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    (getActiveEvaluationStep === 2 || getActiveEvaluationStep === 3 || getActiveEvaluationStep === 4) &&
    userCodEtapa(ctx, null, !!task.mad) === 4
  ) {
    return true;
  }

  // R-12 — realização já expirada por status
  if (task.status === 'apreciacaoExpirada') {
    return false;
  }

  // R-13 — assessor do pit stop: age SÓ na janela 4.1; fora dela, false imediato
  // (intercepta antes dos ramos por cod_etapa)
  if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) {
    if (getActiveEvaluationStep === 4.1) {
      return true;
    } else {
      return false;
    }
  }

  // R-15 — cod2 na janela 2 com taskStep >= 2
  if (userCodEtapa(ctx, null, !!task.mad) === 2 && getActiveEvaluationStep === 2 && (getTaskStepLocal(task) as number) >= 2) {
    return true;
  }

  // R-16 — cod4 na janela 4 com taskStep >= 4
  if (userCodEtapa(ctx, null, !!task.mad) === 4 && getActiveEvaluationStep === 4 && (getTaskStepLocal(task) as number) >= 4) {
    return true;
  }

  // R-17 — cod5 na janela 5 com taskStep >= 5
  if (userCodEtapa(ctx, null, !!task.mad) === 5 && getActiveEvaluationStep === 5 && (getTaskStepLocal(task) as number) >= 5) {
    return true;
  }

  // R-18 — om-dupla cod1/3 nas janelas 2/3: age se taskStep >= janela
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    (getActiveEvaluationStep === 2 || getActiveEvaluationStep === 3) &&
    (userCodEtapa(ctx, null, !!task.mad) === 1 || userCodEtapa(ctx, null, !!task.mad) === 3)
  ) {
    return (getTaskStepLocal(task) as number) >= getActiveEvaluationStep;
  }

  // R-19 — cod1/3 NÃO-om na janela 3 com taskStep >= 3
  if (
    (userCodEtapa(ctx, null, !!task.mad) === 1 || userCodEtapa(ctx, null, !!task.mad) === 3) &&
    String(task.original_manager) !== String(playerInfo._id) &&
    getActiveEvaluationStep === 3 &&
    (getTaskStepLocal(task) as number) >= 3
  ) {
    return true;
  }

  // R-11: ramo inalcançável preservado por paridade (om cod4 nas janelas 2/3 é
  // interceptado pelo ramo vivo de R-11 acima, que retorna true antes)
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    (getActiveEvaluationStep === 2 || getActiveEvaluationStep === 3) &&
    userCodEtapa(ctx, null, !!task.mad) === 4
  ) {
    return (getTaskStepLocal(task) as number) >= getActiveEvaluationStep;
  }

  // R-20 — fallback: om-dupla cod5 com QUALQUER janela ativa
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 5 &&
    !!getActiveEvaluationStep
  ) {
    return true;
  }

  return false;
}

// step2_start (primeiro dia de apreciação) do MÊS a que a realização pertence (OM-03)
// — port de index.tsx:1134-1146. O filtro `type === 'month'` é ESSENCIAL porque
// cyclesExtended também traz os steps trimestral/semestral, cujo intervalo engloba
// o mesmo período (JAN-03: a fase trimestral/semestral herda step2_start de mês
// possivelmente FUTURO — não pode servir de piso para om direto cod4/5).
export function getTaskMonthStep2Start(ctx: NormalRuleContext, task: any): number | undefined {
  const { cyclesExtended } = ctx;

  if (!cyclesExtended || !task) return undefined;

  const step2Start = cyclesExtended
    .find((c: any) => task.created > c.date_start && task.created <= c.date_end)
    ?.steps?.find((s: any) => s.type === 'month' && task.created > s.date_start && task.created <= s.date_finish)
    ?.date_evaluation_step2_start;

  return typeof step2Start === 'number' ? step2Start : undefined;
}

// Gate de bonificação/dobra (D-01..D-07) — port de index.tsx:1431-1557.
export function canDouble(ctx: NormalRuleContext, task: any): boolean {
  const { realDatetime, isVacationManager, getActiveEvaluationStep, doubledTasks, maxDoubledTasks } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // D-01/D-02 — pré-requisitos e cota (exceção om-dupla cod5)
  if (
    !canReset(ctx, task) ||
    !getTaskStepLocal(task) ||
    !activeStageObj ||
    (getTaskStepLocal(task) as number) >= 5 ||
    (getTaskStepLocal(task) as number) === 4.1 ||
    getPlayerStep(ctx, task) === 4.1 ||
    typeof task.multiplicador !== 'number' ||
    typeof task.stageNumber !== 'number' ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    getTaskStepLocal(task) === 2 ||
    getPlayerStep(ctx, task) === 2 ||
    (getTaskStepLocal(task) as number) > getPlayerStep(ctx, task) ||
    (doubledTasks.length >= maxDoubledTasks &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        userCodEtapa(ctx, null, !!task.mad) === 5
      ))
  ) {
    return false;
  }

  // D-03 — Gestor imediato apreciando realização expirada (só taskStep3/mult1 ou taskStep4/mult2)
  if (
    taskAppreciationExpired(ctx, task) &&
    isVacationManager &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) &&
        userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
    ((getTaskStepLocal(task) === 3 && task.multiplicador === 1) || (getTaskStepLocal(task) === 4 && task.multiplicador === 2))
  ) {
    if (
      !getActiveEvaluationStep &&
      ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && realDatetime < activeStageObj.date_evaluation_step4_start) ||
        (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && realDatetime < activeStageObj.date_evaluation_step5_start))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
        (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      userCodEtapa(ctx, null, !!task.mad) === 4 &&
      realDatetime > activeStageObj.date_evaluation_step4_start &&
      (getTaskStepLocal(task) as number) < 4
    ) {
      return true;
    }

    return false;
  }

  // D-04 — om-dupla cod5: bonifica (via ModalCod5) apenas o que aguarda o nível 1
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 5
  ) {
    return task.status === 'aprovado';
  }

  // D-05 — om-dupla cod4 antes de step4_start: bonificação antecipada do executivo original
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 4 &&
    activeStageObj.date_evaluation_step4_start &&
    realDatetime < activeStageObj.date_evaluation_step4_start
  ) {
    if (getTaskStepLocal(task) === 3 && task.multiplicador === 1) {
      return true;
    }
  }

  // D-06 — sem janela ativa
  if (!getActiveEvaluationStep) {
    return false;
  }

  // D-07 — regra geral por janela
  if (getTaskStepLocal(task) === 3 && task.multiplicador === 1 && getActiveEvaluationStep === 3) {
    return true;
  }

  if (getTaskStepLocal(task) === 4 && task.multiplicador === 2 && getActiveEvaluationStep === 4) {
    return true;
  }

  return false;
}

// Gate de promoção (P-01..P-06) — port de index.tsx:1559-1692. O gate roda sobre a
// ORIGINAL task (canReset(originalTask)) e os demais checks sobre a task exibida.
export function canPromote(ctx: NormalRuleContext, task: any, originalTask: any): boolean {
  const { realDatetime, isVacationManager, getActiveEvaluationStep, promotedTasks, maxPromotedTasks } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // P-01 — pré-requisitos e cota (exceção om-dupla cod5)
  if (
    !canReset(ctx, originalTask) ||
    !getTaskStepLocal(task) ||
    !activeStageObj ||
    typeof task.multiplicador !== 'number' ||
    typeof task.stageNumber !== 'number' ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    getTaskStepLocal(task) === 2 ||
    (promotedTasks.length >= maxPromotedTasks &&
      !(
        (String(task.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
        userCodEtapa(ctx, null, !!task.mad) === 5
      ))
  ) {
    return false;
  }

  // P-02 — Gestor imediato promovendo `dobrado` (não exige expirada aqui)
  if (
    isVacationManager &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) &&
        userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
    ((task.status === 'dobrado' && getTaskStepLocal(task) === 3 && task.multiplicador === 2) ||
      (task.status === 'dobrado' && getTaskStepLocal(task) === 4 && task.multiplicador === 4))
  ) {
    if (
      !getActiveEvaluationStep &&
      ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && realDatetime < activeStageObj.date_evaluation_step4_start) ||
        (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && realDatetime < activeStageObj.date_evaluation_step5_start))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep >= 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
        (getActiveEvaluationStep >= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      userCodEtapa(ctx, null, !!task.mad) === 4 &&
      realDatetime > activeStageObj.date_evaluation_step4_start &&
      (getTaskStepLocal(task) as number) < 4
    ) {
      return true;
    }

    return false;
  }

  // P-03 — om-dupla cod4 antes de step4_start: promoção antecipada de dobrado/step3/mult2
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 4 &&
    activeStageObj.date_evaluation_step4_start &&
    realDatetime < activeStageObj.date_evaluation_step4_start
  ) {
    if (task.status === 'dobrado' && getTaskStepLocal(task) === 3 && task.multiplicador === 2) {
      return true;
    }
  }

  if (!getActiveEvaluationStep) {
    return false;
  }

  // P-02 — substituto com cota cheia nunca promove (sem exceção om)
  if (isVacationManager && promotedTasks.length >= maxPromotedTasks) return false;

  // P-04 — om-dupla cod5 promove somente via ModalCod5
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 5
  ) {
    return false;
  }

  // P-06: ramo inalcançável preservado por paridade — exige janela 4, mas o canReset
  // do assessor (R-13) só passa na 4.1; o gate real do botão do assessor é
  // pitStopCanEvaluate no task-item (PIT-05)
  if (
    task.status === 'promovido' &&
    getTaskStepLocal(task) === 4.1 &&
    getPlayerStep(ctx, task) === 4.1 &&
    getActiveEvaluationStep === 4 &&
    task.prosseguir_gst === 'next'
  ) {
    return true;
  }

  // P-05 — regra geral sobre dobrado por janela
  if (task.status === 'dobrado') {
    if (getTaskStepLocal(task) === 3 && task.multiplicador === 2 && getActiveEvaluationStep === 3) {
      return true;
    }

    if (getTaskStepLocal(task) === 4 && task.multiplicador === 4 && getActiveEvaluationStep === 4) {
      return true;
    }

    if (getTaskStepLocal(task) === 5 && task.multiplicador === 8 && getActiveEvaluationStep === 5) {
      return true;
    }
  }

  return false;
}

// P-07 — canPromoteWithNoBonus: port de index.tsx:1762-1829. CÓDIGO MORTO no monólito
// (nenhum call-site em ApreciarRealizacao; único uso no repo está comentado em
// v3/ApreciarAcao) — portado por paridade, com a janela 4.1 correta para o assessor
// e as regras próprias do om cod5 diretor (mult8/janela5).
export function canPromoteWithNoBonus(ctx: NormalRuleContext, task: any): boolean {
  const { isVacationManager, getActiveEvaluationStep, promotedTasks, maxPromotedTasks } = ctx;
  const playerInfo: any = ctx.playerInfo;

  if (
    !canReset(ctx, task) ||
    !getTaskStepLocal(task) ||
    !getActiveEvaluationStep ||
    typeof task.multiplicador !== 'number' ||
    typeof task.stageNumber !== 'number' ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    (getTaskStepLocal(task) !== getActiveEvaluationStep &&
      !(String(task.original_manager) === String(playerInfo._id) && userCodEtapa(ctx, null, !!task.mad) === 5)) ||
    (getActiveEvaluationStep < 3 &&
      !(String(task.original_manager) === String(playerInfo._id) && userCodEtapa(ctx, null, !!task.mad) === 5)) ||
    promotedTasks.length >= maxPromotedTasks
  ) {
    return false;
  }

  if (isVacationManager && promotedTasks.length >= maxPromotedTasks) return false;

  if (String(task.original_manager) === String(playerInfo._id) && userCodEtapa(ctx, null, !!task.mad) === 5) {
    // Presidente e vice-presidente
    if (playerInfo.funcao_localizacao === 3001 || playerInfo.funcao_localizacao === 3002) {
      return false;
    } else {
      // Diretores e gerentes gerais
      if (getActiveEvaluationStep < 5) {
        return false;
      } else {
        return (
          getTaskStepLocal(task) === getActiveEvaluationStep &&
          task.status === 'dobrado' &&
          task.multiplicador &&
          task.multiplicador === 8
        );
      }
    }
  }

  if (
    task.status === 'promovido' &&
    getTaskStepLocal(task) === 4.1 &&
    getPlayerStep(ctx, task) === 4.1 &&
    getActiveEvaluationStep === 4.1 &&
    task.prosseguir_gst === 'next'
  ) {
    return true;
  }

  if (task.status === 'dobrado') {
    if (getTaskStepLocal(task) === 3 && task.multiplicador === 2) {
      return true;
    }

    if (getTaskStepLocal(task) === 4 && task.multiplicador === 4) {
      return true;
    }

    if (getTaskStepLocal(task) === 5 && task.multiplicador === 8) {
      return true;
    }
  }

  return false;
}

export interface AwardAvailability {
  uau: boolean;
  mandou: boolean;
  valeu: boolean;
}

// Gate de prêmios Valeu/Mandou Bem/UAU (A-01..A-05) — port de index.tsx:1694-1748.
export function canAward(ctx: NormalRuleContext, task: any, modal = false): AwardAvailability {
  const { getActiveEvaluationStep, uauTasks, maxUauTasks, mandouBemTasks, maxMandouBemTasks } = ctx;
  const playerInfo: any = ctx.playerInfo;

  const obj = {
    uau: false,
    mandou: false,
    valeu: false,
  };

  // A-01 — bloqueios de entrada
  if (
    !task ||
    !canReset(ctx, task) ||
    typeof task.multiplicador !== 'number' ||
    typeof task.stageNumber !== 'number' ||
    typeof task.status !== 'string' ||
    typeof task.original_manager !== 'string' ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    !task.original_manager.length ||
    !playerInfo?._id
  ) {
    return obj;
  }

  const isOriginalManager =
    String(task.original_manager) === String(playerInfo._id) ||
    (Array.isArray(playerInfo?.mad_dupla) &&
      playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)));

  // A-02 — om-dupla cod5 no ModalCod5: limites reais das cotas, ignorando janela/status
  if (isOriginalManager && userCodEtapa(ctx, null, !!task.mad) && userCodEtapa(ctx, null, !!task.mad) === 5 && !!modal) {
    return {
      uau: Array.isArray(uauTasks) && uauTasks.length < maxUauTasks,
      mandou: Array.isArray(mandouBemTasks) && mandouBemTasks.length < maxMandouBemTasks,
      valeu: true,
    };
  }

  // A-03 — fluxo normal: cod5 + (janela 5 ou sem janela) + promovido
  if (
    userCodEtapa(ctx, null, !!task.mad) !== 5 ||
    (getActiveEvaluationStep && getActiveEvaluationStep !== 5) ||
    task.status !== 'promovido'
  ) {
    return obj;
  }

  const taskPoints = calcPoints(task.multiplicador, task.status, task.stageNumber);

  // A-04 — já premiada
  if (taskPoints >= 1500) {
    return obj;
  }

  // A-05 — valeu sempre; mandou/uau por cota
  obj.uau = Array.isArray(uauTasks) && uauTasks.length < maxUauTasks;
  obj.mandou = Array.isArray(mandouBemTasks) && mandouBemTasks.length < maxMandouBemTasks;
  obj.valeu = true;

  return obj;
}

// Gate de edição (E-01..E-05) — port de index.tsx:1831-1923.
export function canEdit(ctx: NormalRuleContext, task: any): boolean {
  const { realDatetime, isVacationManager, getActiveEvaluationStep } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // E-01 — bloqueios
  if (
    !activeStageObj ||
    !canReset(ctx, task) ||
    typeof userCodEtapa(ctx, null, !!task.mad) !== 'number' ||
    (userCodEtapa(ctx, null, !!task.mad) as number) < 1 ||
    (userCodEtapa(ctx, null, !!task.mad) as number) > 5 ||
    task.status === 'concluido' ||
    task.status === 'concluidoAuto' ||
    task.status === 'apreciacaoExpirada'
  ) {
    return false;
  }

  // E-02 — Gestor imediato apreciando realização expirada (LAT-05 SEM o sub-ramo
  // extra do cod4 pós-step4_start — diferença deliberada vs canReset/canEvaluate)
  if (
    taskAppreciationExpired(ctx, task) &&
    isVacationManager &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) && userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor)))
  ) {
    if (
      !getActiveEvaluationStep &&
      ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && realDatetime < activeStageObj.date_evaluation_step4_start) ||
        (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && realDatetime < activeStageObj.date_evaluation_step5_start))
    ) {
      return true;
    } else if (
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 3 &&
        (userCodEtapa(ctx, userInfo, !!task.mad) === 1 || userCodEtapa(ctx, userInfo, !!task.mad) === 3)) ||
        (getActiveEvaluationStep === 3 &&
          userCodEtapa(ctx, userInfo, !!task.mad) === 4 &&
          (userCodEtapa(ctx, playerInfo, !!task.mad) === 1 ||
            // Gerente de equipe substituído (cod_etapa 2): valida realização expirada na step3
            userCodEtapa(ctx, playerInfo, !!task.mad) === 2 ||
            userCodEtapa(ctx, playerInfo, !!task.mad) === 3)) ||
        (getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
        (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
    ) {
      return true;
    }

    return false;
  }

  // E-03: om cod5 SEM verificação de dupla (assimetria vs canReset/canEvaluate,
  // preservada por paridade)
  if (
    String(task.original_manager) === String(playerInfo._id) &&
    userCodEtapa(ctx, null, !!task.mad) === 5 &&
    realDatetime <= activeStageObj.date_evaluation_step5_finish
  ) {
    return true;
  }

  // E-04 — om-dupla cod4 antes de step4_start
  if (
    (String(task.original_manager) === String(playerInfo._id) ||
      (Array.isArray(playerInfo?.mad_dupla) &&
        playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
    userCodEtapa(ctx, null, !!task.mad) === 4 &&
    activeStageObj.date_evaluation_step4_start &&
    realDatetime < activeStageObj.date_evaluation_step4_start
  ) {
    return true;
  }

  // E-05 — regra geral por janela
  if (!getActiveEvaluationStep) {
    return false;
  }

  if (task.status === 'aguardando') {
    return true;
  }

  if (task.multiplicador === 1 && getActiveEvaluationStep === 3 && getTaskStepLocal(task) === 3) {
    return true;
  }

  if (task.status === 'promovido' && task.multiplicador === 2 && getActiveEvaluationStep === 4) {
    return true;
  }

  if (task.status === 'promovido' && task.multiplicador === 4 && getActiveEvaluationStep === 5) {
    return true;
  }

  return false;
}

// Gate de apreciação (V-01..V-07) — port de index.tsx:1925-2056.
export function canEvaluate(ctx: NormalRuleContext, item: any): boolean {
  const { realDatetime, isVacationManager, getActiveEvaluationStep } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // V-07 — qualquer erro em runtime resulta false
  try {
    const dateStart = activeStageObj?.date_evaluation_step2_start;
    const dateFinish = activeStageObj?.date_evaluation_step5_finish
      ? activeStageObj.date_evaluation_step5_finish
      : activeStageObj?.date_evaluation_step4_finish
        ? activeStageObj.date_evaluation_step4_finish
        : activeStageObj?.date_evaluation_step3_finish
          ? activeStageObj?.date_evaluation_step3_finish
          : activeStageObj?.date_evaluation_step2_finish
            ? activeStageObj?.date_evaluation_step2_finish
            : null;

    // V-02 — Gestor imediato apreciando realização expirada (LAT-05 + ramo extra cod4)
    if (
      taskAppreciationExpired(ctx, item) &&
      isVacationManager &&
      (userInfo?._id === playerInfo?.gestor ||
        (Array.isArray(userInfo?.mad_dupla) &&
          userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
      activeStageObj
    ) {
      if (
        !getActiveEvaluationStep &&
        ((userCodEtapa(ctx, userInfo, !!item.mad) === 4 && realDatetime < activeStageObj.date_evaluation_step4_start) ||
          (userCodEtapa(ctx, userInfo, !!item.mad) === 5 && realDatetime < activeStageObj.date_evaluation_step5_start))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        ((getActiveEvaluationStep === 3 &&
          (userCodEtapa(ctx, userInfo, !!item.mad) === 1 || userCodEtapa(ctx, userInfo, !!item.mad) === 3)) ||
          (getActiveEvaluationStep === 3 &&
            userCodEtapa(ctx, userInfo, !!item.mad) === 4 &&
            (userCodEtapa(ctx, playerInfo, !!item.mad) === 1 ||
              // Gerente de equipe substituído (cod_etapa 2): valida realização expirada na step3
              userCodEtapa(ctx, playerInfo, !!item.mad) === 2 ||
              userCodEtapa(ctx, playerInfo, !!item.mad) === 3)) ||
          (getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!item.mad) === 4) ||
          (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!item.mad) === 5))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        userCodEtapa(ctx, null, !!item.mad) === 4 &&
        realDatetime > activeStageObj.date_evaluation_step4_start &&
        (getTaskStepLocal(item) as number) < 4
      ) {
        return true;
      }

      return false;
    }

    // V-03: om cod5 SEM verificação de dupla (assimetria vs canReset, preservada
    // por paridade)
    if (
      String(item.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!item.mad) === 5 &&
      activeStageObj &&
      realDatetime <= activeStageObj.date_evaluation_step5_finish
    ) {
      return true;
    }

    // V-04 — om-dupla cod4 antes de step4_start
    if (
      (String(item.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)))) &&
      userCodEtapa(ctx, null, !!item.mad) === 4 &&
      activeStageObj &&
      activeStageObj.date_evaluation_step4_start &&
      realDatetime < activeStageObj.date_evaluation_step4_start
    ) {
      return true;
    }

    if (!getActiveEvaluationStep || !activeStageObj) {
      return false;
    }

    // V-01 — intervalo global da fase
    if (
      !!activeStageObj &&
      typeof userCodEtapa(ctx, null, !!item.mad) === 'number' &&
      !!getActiveEvaluationStep &&
      dateStart &&
      dateFinish &&
      realDatetime >= dateStart &&
      realDatetime <= dateFinish
    ) {
      // V-05 — om-dupla cod5 (alcançável apenas via dupla; om direto saiu em V-03)
      if (
        (String(item.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)))) &&
        userCodEtapa(ctx, null, !!item.mad) === 5
      ) {
        // Presidente e vice-presidente
        if (playerInfo.funcao_localizacao === 3001 || playerInfo.funcao_localizacao === 3002) {
          return true;
        } else {
          // Diretores e gerentes gerais
          if (getActiveEvaluationStep < 5) {
            return true;
          } else {
            if (getTaskStepLocal(item) === 2 && getActiveEvaluationStep === 2) return true;
            else if (getTaskStepLocal(item) === 3 && getActiveEvaluationStep === 3) return true;
            else if (getTaskStepLocal(item) === 4 && getActiveEvaluationStep === 4) return true;
            else if (getTaskStepLocal(item) === 4.1 && getActiveEvaluationStep === 4.1) return true;
            else if (getTaskStepLocal(item) === 5 && getActiveEvaluationStep === 5) return true;
            else return false;
          }
        }
      } else {
        // V-06 — demais perfis: taskStep === janela exato
        if (getTaskStepLocal(item) === 2 && getActiveEvaluationStep === 2) return true;
        else if (getTaskStepLocal(item) === 3 && getActiveEvaluationStep === 3) return true;
        else if (getTaskStepLocal(item) === 4 && getActiveEvaluationStep === 4) return true;
        else if (getTaskStepLocal(item) === 4.1 && getActiveEvaluationStep === 4.1) return true;
        else if (getTaskStepLocal(item) === 5 && getActiveEvaluationStep === 5) return true;
        else return false;
      }
    } else {
      return false;
    }
  } catch (e) {
    return false;
  }
}

// Alçada de aprovação de quantidade de participantes (PART-01) — port de
// index.tsx:2259-2286. Já era pura no monólito: recebe o PLAYER avaliador e a task.
export function canApproveParticipantsCount(player: any, task: any): boolean {
  if (task?.participants_approval?.status !== 'pending') {
    return false;
  }

  const total = Array.isArray(task?.team) ? task.team.length : !!task?.qtdTeam ? task.qtdTeam : 0;
  const ro = typeof player?.ro === 'string' ? player.ro : '';
  const codFuncao = player?.cod_funcao;
  const isDirectorOrAbove = codFuncao === 3003 || codFuncao === 4005 || codFuncao === 4007 || codFuncao === 3002;

  if ((!!ro.match(/(3GUE|2GUT|2GCA|2GUA|2GUE|1GUT|1GUA|1GCA)/gi) || isDirectorOrAbove) && total >= 21 && total <= 30) {
    return true;
  }

  if ((!!ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi) || isDirectorOrAbove) && total >= 31 && total <= 50) {
    return true;
  }

  if (isDirectorOrAbove && total >= 51 && total <= 100) {
    return true;
  }

  if (codFuncao === 3002 && total > 100) {
    return true;
  }

  return false;
}

// Feedback do usuário logado na task — port de index.tsx:3434-3449.
// O feedback guarda a CHAVE (prefixo F) de quem o escreveu (`evaluatorId`) e de quem foi
// substituído (`original_task_manager.playerId`), então a identidade do logado é
// `userInfo.chave` — o mesmo valor do `user._id` que o monólito usava. O `_id` do
// player_fields é a matrícula crua (sem o F e sem zeros à esquerda) e nunca casa.
export function hasTaskFeedbackForUser(ctx: NormalRuleContext, task: any): boolean {
  const userKey = ctx.userInfo?.chave;

  if (!userKey || !Array.isArray(task?.feedback)) return false;

  return !!task.feedback.find(
    (x: any) =>
      (x.evaluatorId === userKey && !x.original_task_manager?.playerId) ||
      (x.original_task_manager?.playerId && x.original_task_manager?.playerId === userKey) ||
      (x.original_task_manager?.playerId &&
        x.evaluatorId === userKey &&
        ((Array.isArray(task.evaluator_detail) && task.evaluator_detail.find((y: any) => y.chave === userKey)) ||
          (Array.isArray(task.non_evaluator_detail) && task.non_evaluator_detail.find((y: any) => y.chave === userKey)))),
  );
}

// Labels de status por nível (UI-08) — port 1:1 de index.tsx:461-698. Resolve o step
// da task por `realCycle` e usa `task.now` como relógio (hora do servidor embutida
// pelo aggregate). Retorna a lista deduplicada de labels ou undefined.
export function getTaskStatuses(ctx: NormalRuleContext, task: any): string[] | undefined {
  const { realCycle } = ctx;

  if (!task || !realCycle) return undefined;

  const statuses: any[] = [];

  const taskStep = getTaskStepLocal(task);

  // `any` local: no monólito realCycle/steps são tipados sem opcionais; a resolução
  // (incluindo o acesso não-opcional a `.steps`) mantém a semântica JS original.
  const stepTask: any = (realCycle as any[])
    .find((x: any) => task.created > x.date_start && task.created <= x.date_end)
    ?.steps.find((x: any) => task.created > x.date_start && task.created <= x.date_finish);
  const actualTime = task.now;

  if (!stepTask || !taskStep) return undefined;

  function upsertStatus(newStatus: Record<string, string>) {
    const key = Object.keys(newStatus)[0];
    const index = statuses.findIndex((item: any) => Object.keys(item)[0] === key);

    if (index !== -1) {
      statuses[index] = newStatus;
    } else {
      statuses.push(newStatus);
    }
  }

  // Gerente de equipe
  if (task.status === 'aguardando' && actualTime <= stepTask.date_evaluation_step2_finish) {
    upsertStatus({ stage0: 'Aguardando validação' });
  } else if (task.status === 'aguardando' && actualTime > stepTask.date_evaluation_step2_finish) {
    upsertStatus({ stage0: 'Prazo expirado' });
  } else if (task.status === 'rejeitado') {
    upsertStatus({ stage0: 'Devolvida' });
  } else if (task.multiplicador && task.multiplicador >= 1) {
    upsertStatus({ stage0: 'Validada' });
  }

  // Nível 1
  if (taskStep > 2) {
    if (
      task.status === 'aprovado' &&
      task.multiplicador &&
      task.multiplicador === 1 &&
      actualTime <= stepTask.date_evaluation_step3_finish
    ) {
      upsertStatus({ stage1: 'Aguardando apreciação' });
    } else if (
      task.status === 'aprovado' &&
      task.multiplicador &&
      task.multiplicador === 1 &&
      actualTime > stepTask.date_evaluation_step3_finish
    ) {
      upsertStatus({ stage1: 'Prazo expirado' });
    } else if (task.status === 'concluido' && task.multiplicador && task.multiplicador === 1 && taskStep === 3) {
      upsertStatus({ stage1: 'Validada - Finalizada' });
    } else if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      actualTime <= stepTask.date_evaluation_step3_finish
    ) {
      upsertStatus({ stage1: 'Bonificada' });
    } else if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      actualTime > stepTask.date_evaluation_step3_finish
    ) {
      upsertStatus({ stage1: 'Bonificada - Prazo expirado' });
    } else if (
      (task.status === 'concluido' || task.status === 'concluidoAuto') &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      taskStep === 3
    ) {
      upsertStatus({ stage1: 'Bonificada - Finalizada' });
    } else if (task.multiplicador && task.multiplicador >= 2 && taskStep > 3) {
      upsertStatus({ stage1: 'Promovida' });
    }
  }

  // Nível 2
  if (taskStep > 3) {
    if (
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      (!stepTask.date_evaluation_step4_finish || actualTime <= stepTask.date_evaluation_step4_finish)
    ) {
      upsertStatus({ stage2: 'Aguardando apreciação' });
    } else if (
      stepTask.date_evaluation_step4_finish &&
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 2 &&
      actualTime > stepTask.date_evaluation_step4_finish
    ) {
      upsertStatus({ stage2: 'Prazo expirado' });
    } else if (task.status === 'concluido' && task.multiplicador && task.multiplicador === 2 && taskStep === 4) {
      upsertStatus({ stage2: 'Promovida ao nível 2 - Finalizada' });
    } else if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      (!stepTask.date_evaluation_step4_finish || actualTime <= stepTask.date_evaluation_step4_finish)
    ) {
      upsertStatus({ stage2: 'Bonificada' });
    } else if (
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      actualTime > stepTask.date_evaluation_step4_finish
    ) {
      upsertStatus({ stage2: 'Bonificada - Prazo expirado' });
    } else if (
      (task.status === 'concluido' || task.status === 'concluidoAuto') &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      taskStep === 4
    ) {
      upsertStatus({ stage2: 'Bonificada - Finalizada' });
    } else if (task.multiplicador && task.multiplicador >= 4 && taskStep > 4) {
      upsertStatus({ stage2: 'Promovida' });
    }
  }

  // Nível 3
  if (taskStep > 4) {
    if (
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      (!stepTask.date_evaluation_step5_finish || actualTime <= stepTask.date_evaluation_step5_finish)
    ) {
      upsertStatus({ stage3: 'Aguardando apreciação' });
    } else if (
      stepTask.date_evaluation_step5_finish &&
      task.status === 'promovido' &&
      task.multiplicador &&
      task.multiplicador === 4 &&
      actualTime > stepTask.date_evaluation_step5_finish
    ) {
      upsertStatus({ stage3: 'Prazo expirado' });
    } else if (task.status === 'concluido' && task.multiplicador && task.multiplicador === 4) {
      upsertStatus({ stage3: 'Promovida ao nível 3 - Finalizada' });
    } else if (
      stepTask.date_evaluation_step5_finish &&
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 8 &&
      actualTime <= stepTask.date_evaluation_step5_finish
    ) {
      upsertStatus({ stage3: 'Bonificada' });
    } else if (
      stepTask.date_evaluation_step5_finish &&
      task.status === 'dobrado' &&
      task.multiplicador &&
      task.multiplicador === 8 &&
      actualTime > stepTask.date_evaluation_step5_finish
    ) {
      upsertStatus({ stage3: 'Bonificada - Prazo expirado' });
    } else if (
      (task.status === 'concluido' || task.status === 'concluidoAuto') &&
      task.multiplicador &&
      task.multiplicador === 8
    ) {
      upsertStatus({ stage3: 'Bonificada - Finalizada' });
    } else if (task.status === 'selo' || task.status === 'mandou' || task.status === 'valeu') {
      upsertStatus({
        stage3: `${task.status === 'selo' ? 'Bonificada UAU' : task.status === 'mandou' ? 'Bonificada Mandou Bem' : task.status === 'valeu' ? 'Bonificada Valeu' : 'Premiada'} - Finalizada`,
      });
    }
  }

  // om cod5: sobrescreve os labels de espera enquanto o prazo dele não estourou
  if (
    !!userCodEtapa(ctx, task.original_manager_detail, !!task.mad) &&
    userCodEtapa(ctx, task.original_manager_detail, !!task.mad) === 5
  ) {
    if (task.status === 'aguardando' && actualTime <= stepTask.date_evaluation_step5_finish) {
      upsertStatus({ stage0: 'Aguardando validação' });
    }

    if (
      taskStep > 2 &&
      task.status === 'aprovado' &&
      task.multiplicador &&
      task.multiplicador === 1 &&
      actualTime <= stepTask.date_evaluation_step5_finish
    ) {
      upsertStatus({ stage1: 'Aguardando apreciação' });
    }
  }

  // om cod4: idem, com teto em step4_start
  if (
    !!userCodEtapa(ctx, task.original_manager_detail, !!task.mad) &&
    userCodEtapa(ctx, task.original_manager_detail, !!task.mad) === 4
  ) {
    if (task.status === 'aguardando' && stepTask.date_evaluation_step4_start && actualTime <= stepTask.date_evaluation_step4_start) {
      upsertStatus({ stage0: 'Aguardando validação' });
    }

    if (
      taskStep > 2 &&
      task.status === 'aprovado' &&
      task.multiplicador &&
      task.multiplicador === 1 &&
      stepTask.date_evaluation_step4_start &&
      actualTime <= stepTask.date_evaluation_step4_start
    ) {
      upsertStatus({ stage1: 'Aguardando apreciação' });
    }
  }

  const values = statuses.map(item => Object.values(item)[0]);
  return values.filter((value, index, self) => self.indexOf(value) === index) as string[];
}
